import {
  ArrowDownRight,
  ArrowUpRight,
  Baby,
  BookOpen,
  Briefcase,
  Bus,
  Car,
  CircleEllipsis,
  Clapperboard,
  Coffee,
  Dumbbell,
  Fuel,
  Gift,
  GraduationCap,
  HeartPulse,
  House,
  Lightbulb,
  PawPrint,
  Pill,
  Plane,
  Receipt,
  Repeat,
  Scissors,
  Shirt,
  ShoppingBag,
  ShoppingCart,
  Smartphone,
  Sparkles,
  Train,
  Users,
  Utensils,
  Wifi,
  Wrench,
  X,
  type LucideIcon,
} from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react';

import { categoryPalette, type CategoryColor } from '@tokens';

// ── Buttons ──────────────────────────────────────────────────────────────────

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'md' | 'lg';
  block?: boolean;
  icon?: LucideIcon;
  loading?: boolean;
};

export function Button({ variant = 'primary', size = 'md', block, icon: Icon, loading, children, className = '', disabled, ...rest }: ButtonProps) {
  return (
    <button
      className={`btn btn-${variant} ${size === 'lg' ? 'btn-lg' : ''} ${block ? 'btn-block' : ''} ${className}`}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}>
      {loading ? <span className="spinner" style={{ width: 18, height: 18 }} /> : Icon && <Icon size={18} />}
      {children}
    </button>
  );
}

export function IconButton({ icon: Icon, label, ...rest }: { icon: LucideIcon; label: string } & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button className="icon-btn" aria-label={label} title={label} {...rest}>
      <Icon size={20} />
    </button>
  );
}

export function Chip({ on, children, ...rest }: { on?: boolean; children: ReactNode } & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button className={`chip ${on ? 'on' : ''}`} aria-pressed={on} {...rest}>
      {children}
    </button>
  );
}

// ── Category visuals ─────────────────────────────────────────────────────────

export const CATEGORY_ICONS: Record<string, LucideIcon> = {
  utensils: Utensils, coffee: Coffee, 'shopping-cart': ShoppingCart, 'shopping-bag': ShoppingBag, shirt: Shirt,
  bus: Bus, car: Car, train: Train, fuel: Fuel, plane: Plane, receipt: Receipt, lightbulb: Lightbulb, wifi: Wifi,
  smartphone: Smartphone, house: House, wrench: Wrench, 'heart-pulse': HeartPulse, pill: Pill, dumbbell: Dumbbell,
  scissors: Scissors, clapperboard: Clapperboard, sparkles: Sparkles, 'graduation-cap': GraduationCap,
  'book-open': BookOpen, briefcase: Briefcase, repeat: Repeat, users: Users, baby: Baby, 'paw-print': PawPrint,
  gift: Gift, 'circle-ellipsis': CircleEllipsis,
};

function useScheme(): 'light' | 'dark' {
  const query = '(prefers-color-scheme: dark)';
  const [dark, setDark] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const m = window.matchMedia(query);
    const on = () => setDark(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, []);
  return dark ? 'dark' : 'light';
}

export function useCategoryColors(color: string) {
  const scheme = useScheme();
  return (categoryPalette[color as CategoryColor] ?? categoryPalette.slate)[scheme];
}

export function CategoryBadge({ icon, color, size = 36 }: { icon: string; color: string; size?: number }) {
  const c = useCategoryColors(color);
  const Icon = CATEGORY_ICONS[icon] ?? CircleEllipsis;
  return (
    <span className="cat-badge" style={{ width: size, height: size, background: c.bg }} aria-hidden>
      <Icon size={Math.round(size * 0.5)} color={c.fg} strokeWidth={2.1} />
    </span>
  );
}

// ── Feedback ─────────────────────────────────────────────────────────────────

export function Delta({ percent, suffix }: { percent: number; suffix: string }) {
  const up = percent > 0;
  return (
    <span className="row" style={{ gap: 8 }}>
      <span className={`delta ${up ? 'up' : 'down'}`}>
        {up ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
        {Math.abs(percent)}%
      </span>
      <span className="muted" style={{ fontSize: 13 }}>
        {up ? 'more' : 'less'} than {suffix}
      </span>
    </span>
  );
}

export function EmptyState({ icon: Icon, title, text, action }: { icon: LucideIcon; title: string; text?: string; action?: ReactNode }) {
  return (
    <div className="empty">
      <div className="icon">
        <Icon size={28} />
      </div>
      <h3>{title}</h3>
      {text && <p>{text}</p>}
      {action}
    </div>
  );
}

export function Spinner() {
  return <span className="spinner" role="status" aria-label="Loading" />;
}

// ── Drawer ───────────────────────────────────────────────────────────────────

export function Drawer({ title, onClose, children, footer }: { title: string; onClose: () => void; children: ReactNode; footer?: ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);
  return (
    <>
      <div className="overlay" onClick={onClose} />
      <aside className="drawer" role="dialog" aria-modal="true" aria-label={title}>
        <div className="drawer-head">
          <h2>{title}</h2>
          <IconButton icon={X} label="Close" onClick={onClose} />
        </div>
        <div className="drawer-body">{children}</div>
        {footer && <div className="drawer-foot">{footer}</div>}
      </aside>
    </>
  );
}

// ── Toasts ───────────────────────────────────────────────────────────────────

type Toast = { id: number; message: string; action?: { label: string; run: () => void } };
const ToastCtx = createContext<(t: Omit<Toast, 'id'>) => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const show = useCallback((t: Omit<Toast, 'id'>) => {
    const id = Date.now() + Math.random();
    setToasts((all) => [...all.slice(-2), { ...t, id }]);
    setTimeout(() => setToasts((all) => all.filter((x) => x.id !== id)), t.action ? 5000 : 3000);
  }, []);
  return (
    <ToastCtx.Provider value={show}>
      {children}
      <div className="toast-host" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className="toast">
            {t.message}
            {t.action && (
              <button
                onClick={() => {
                  t.action!.run();
                  setToasts((all) => all.filter((x) => x.id !== t.id));
                }}>
                {t.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export const useToast = () => useContext(ToastCtx);
